import { Component } from '@theme/component';
import { CartLinesUpdateEvent } from '@shopify/events';

/** @extends {Component<{status: HTMLElement, cartLink: HTMLAnchorElement}>} */
class NFFSizeCart extends Component {
  requiredRefs = ['status', 'cartLink'];
  #controller;
  #busy = false;

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#busy = false;
  }

  /** @param {SubmitEvent} event */
  async addSize(event) {
    const button = event.submitter;
    const form = button?.form;
    if (!(form instanceof HTMLFormElement) || !(button instanceof HTMLButtonElement)) return;
    event.preventDefault();
    event.stopPropagation();
    if (this.#busy) return;
    this.#busy = true;
    this.#controller = new AbortController();
    const buttons = Array.from(this.querySelectorAll('.nff-size-cart-button'));
    const body = new FormData(form);
    const variantId = String(body.get('id'));
    const quantity = Number(body.get('quantity')) || 1;
    const sizeLabel = button.dataset.sizeLabel || button.textContent.trim();
    const { status, cartLink } = this.refs;
    status.removeAttribute('data-error');
    status.textContent = this.dataset.adding;
    cartLink.hidden = true;
    for (const control of buttons) control.disabled = true;
    const sectionIds = Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
      .map(element => element.dataset.sectionId);
    if (sectionIds.length) body.set('sections', [...new Set(sectionIds)].join(','));
    let added = false;
    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST', body, headers: { Accept: 'application/json' },
        credentials: 'same-origin', signal: this.#controller.signal
      });
      const result = await response.json();
      if (!response.ok || result.status) {
        status.setAttribute('data-error', '');
        status.textContent = typeof result.description === 'string' ? result.description : this.dataset.error;
        return;
      }
      added = true;
      status.textContent = this.dataset.added + ' · ' + sizeLabel;
      cartLink.hidden = false;
      try {
        const cartResponse = await fetch(Theme.routes.cart_url + '.js', {
          headers: { Accept: 'application/json' }, credentials: 'same-origin',
          signal: this.#controller.signal
        });
        if (!cartResponse.ok) return;
        const cart = await cartResponse.json();
        for (const link of document.querySelectorAll('[data-nff-cart-link]')) {
          link.textContent = link.dataset.label + (cart.item_count ? ' (' + cart.item_count + ')' : '');
        }
        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(new CartLinesUpdateEvent({
          action: 'add', context: 'product',
          lines: [{ merchandiseId: variantId, quantity }],
          promise: deferred.promise
        }));
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { items: cart.items, sections: result.sections, didError: false,
            source: 'nff-size-cart', itemCount: quantity }
        });
      } catch {
        // The add succeeded; a failed cart refresh must never invite a duplicate addition.
      }
    } catch (error) {
      if (error.name !== 'AbortError' && !added) {
        status.setAttribute('data-error', '');
        status.textContent = this.dataset.uncertain;
        cartLink.hidden = false;
      }
    } finally {
      this.#busy = false;
      for (const control of buttons) control.disabled = false;
      if (this.isConnected && !document.querySelector('dialog[open]')) button.focus({ preventScroll: true });
    }
  }
}
if (!customElements.get('nff-size-cart')) customElements.define('nff-size-cart', NFFSizeCart);
